import { guardTab } from "@/hooks/useDeskNavigate";
import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import StickyTop from "@/components/common/StickyTop";
import { toast } from "@/lib/toast";
import { Upload, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { hasRight } from "@/components/crm/crmShared";
import { useBrand } from "@/context/BrandContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { SettingsTabBar, TAB_CSS } from "@/components/settings/SettingsTabs";
import UsersSection from "@/components/settings/UsersSection";
import LoginHistorySection from "@/components/settings/LoginHistorySection";
import AccountSection from "@/components/settings/AccountSection";
import SecuritySection from "@/components/settings/SecuritySection";
import TrustedRangesSection from "@/components/settings/TrustedRangesSection";
import StorageSection from "@/components/settings/StorageSection";
import BackupSection from "@/components/settings/BackupSection";
import NasSyncPanel from "@/components/settings/NasSyncPanel";
import EmailPrefsSection from "@/components/settings/EmailPrefsSection";
import CompanyInfoSection from "@/components/settings/CompanyInfoSection";
import ProjectsChoicesSection from "@/components/settings/ProjectsChoicesSection";
import NotifSoundPrefsSection from "@/components/settings/NotifSoundPrefsSection";
import SystemHealthPanel from "@/components/settings/SystemHealthPanel";
import DomainSection from "@/components/settings/DomainSection";
import HolidaysSection from "@/components/settings/HolidaysSection";
import HeaderProfileGroup from "@/components/TopBar";
import api from "@/lib/api";

export default function Settings() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  // iter189 — chaque sous-onglet est câblé à sa clé settings.* (matrice effective renvoyée par /auth/me)
  const R = (k) => hasRight(user, `settings.${k}`);
  const TAB_KEY = { users: "users_and_rights", history: "login_history", brand: "logo_coordinates", "email-prefs": "email_notifications", "email-templates": "email_templates", "notif-sound": "notification_sounds", storage: "storage", "backup-b2": "backblaze", "backup-nas": "nas", health: "health", domain: "domain" };
  // Spec iter94 (Feb 2026) — Stockage & Sauvegardes : accessible à TOUS les
  // admins en lecture. Le Premier Admin conserve l'exclusivité des actions
  // (voir le gate `canWriteBackupStorage` propagé dans les sous-composants).
  const canSeeStorage = R("storage");
  const canSeeBackup = R("backblaze") || R("nas");
  const canWriteBackupStorage = R("backblaze") || R("nas");
  // Spec 2026-07-20 — onglet Notifications email : Admin peut voir, Premier Admin seul peut modifier.
  const canSeeEmailPrefs = R("email_notifications");
  const [params, setParams] = useSearchParams();

  const validTabs = ["account", "security", "users", "history", "brand", "crm", "email-prefs", "notif-sound", "storage", "backup-b2", "backup-nas", "health", "domain", "projects", "holidays"];
  // Rétrocompat : ancienne URL ?tab=backup → backup-b2 (spec 2026-08-02 point #1).
  const rawTab = params.get("tab");
  const normalizedTab = rawTab === "backup" ? "backup-b2" : rawTab;
  const initialTab = validTabs.includes(normalizedTab) ? normalizedTab : "account";
  const [tab, setTab] = useState(initialTab);

  useEffect(() => {
    if (TAB_KEY[tab] && !R(TAB_KEY[tab])) {
      setTab("account");
    }
    if (tab === "storage" && !canSeeStorage) {
      setTab("account");
    }
    if ((tab === "backup-b2" || tab === "backup-nas") && !canSeeBackup) {
      setTab("account");
    }
  }, [isAdmin, tab, canSeeStorage, canSeeBackup]);

  const changeTab = (v) => {
    setTab(v);
    const next = new URLSearchParams(params);
    next.set("tab", v);
    setParams(next, { replace: true });
  };

  return (
    <div data-testid="settings-page">
      <Tabs value={tab} onValueChange={guardTab(changeTab)} className="w-full">
      <StickyTop gap={0} testId="settings-sticky-top">
      <div className="flex items-start justify-between gap-4 mb-2">
        <h1 className="text-4xl font-bold">Paramètres</h1>
        <HeaderProfileGroup />
      </div>
      <p className="text-muted-foreground mb-8">
        Gérez votre profil, les utilisateurs et l&apos;identité visuelle de Vertec.
      </p>

        <style>{TAB_CSS}</style>
        <SettingsTabBar active={tab} onSelect={changeTab} tabs={[
            ["account", "Mon profil", true], ["security", "Sécurité", true], ["users", "Utilisateurs & droits d'accès", R("users_and_rights")], ["history", "Historique de connexion", R("login_history")],
            ["brand", "Logo & coordonnées", R("logo_coordinates")], ["email-prefs", "Notifications email", canSeeEmailPrefs],
            ["notif-sound", "Son des notifications", R("notification_sounds")], ["storage", "Stockage", canSeeStorage], ["backup-b2", "Backblaze B2", canSeeBackup], ["backup-nas", "NAS Synology", canSeeBackup],
            ["health", "Santé", R("health")], ["domain", "Domaine", R("domain")], ["holidays", "Jours fériés", true], ["projects", "Sièges & projets", !!user?.is_first_admin],
          ].filter(([, , ok]) => ok)} />
        </StickyTop>

        <TabsContent value="account" className="mt-6">
          <AccountSection />
        </TabsContent>
        {!!user?.is_first_admin && (
          <TabsContent value="projects" className="mt-6">
            <ProjectsChoicesSection />
          </TabsContent>
        )}
        <TabsContent value="security" className="mt-6">
          <SecuritySection />
          {R("ip_ranges") && <TrustedRangesSection />}
        </TabsContent>
        {R("users_and_rights") && (
          <TabsContent value="users" className="mt-6">
            <UsersSection currentUser={user} />
          </TabsContent>
        )}
        {R("login_history") && (
          <TabsContent value="history" className="mt-6">
            <LoginHistorySection />
          </TabsContent>
        )}
        <TabsContent value="brand" className="mt-6">
          <BrandSection isAdmin={isAdmin} />
        </TabsContent>
        {canSeeEmailPrefs && (
          <TabsContent value="email-prefs" className="mt-6">
            <EmailPrefsSection />
          </TabsContent>
        )}
        {/* Modèles d'emails : tous les contextes (réservation / vente / lot / contact) sont commerciaux → section déplacée dans Commercial › Configuration. Onglet masqué ici. */}
        {R("notification_sounds") && (
          <TabsContent value="notif-sound" className="mt-6">
            <NotifSoundPrefsSection />
          </TabsContent>
        )}
        {canSeeStorage && (
          <TabsContent value="storage" className="mt-6">
            <StorageSection />
          </TabsContent>
        )}
        {canSeeBackup && (
          <TabsContent value="backup-b2" className="mt-6">
            <BackupSection />
          </TabsContent>
        )}
        {canSeeBackup && (
          <TabsContent value="backup-nas" className="mt-6">
            <div className="space-y-4">
              <div>
                <h2 className="text-2xl font-semibold mb-1">NAS Synology <span className="text-sm text-muted-foreground font-normal">(miroir local)</span></h2>
                <p className="text-sm text-muted-foreground max-w-3xl">
                  Miroir local des sauvegardes alimenté par le NAS via une tâche planifiée
                  (Synology Task Scheduler + curl). État reporté toutes les 6 h.
                </p>
              </div>
              <NasSyncPanel />
            </div>
          </TabsContent>
        )}
        {R("health") && (
          <TabsContent value="health" className="mt-6">
            <SystemHealthPanel />
          </TabsContent>
        )}
        {R("domain") && (
          <TabsContent value="domain" className="mt-6">
            <DomainSection />
          </TabsContent>
        )}
        <TabsContent value="holidays" className="mt-6">
          <HolidaysSection />
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* ------------------------------------------------------------------ Brand */
function BrandSection({ isAdmin }) {
  const { brand, refreshBrand } = useBrand();
  const [saving, setSaving] = useState(false);
  const lightRef = useRef(null);
  const darkRef = useRef(null);

  const readFile = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const uploadLogo = async (variant, file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Fichier trop lourd", { description: "Max 2 Mo" });
      return;
    }
    setSaving(true);
    try {
      const dataUrl = await readFile(file);
      await api.put("/settings/brand", { [`logo_${variant}`]: dataUrl });
      await refreshBrand();
      toast.success("Logo mis à jour");
    } catch (err) {
      toast.error("Échec de l'upload", { description: err.response?.data?.detail || "Réessayez." });
    } finally {
      setSaving(false);
    }
  };

  const resetLogo = async (variant) => {
    setSaving(true);
    try {
      await api.delete(`/settings/brand/logo/${variant}`);
      await refreshBrand();
      toast.success("Logo réinitialisé");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="bg-card border-vertec-borderDark p-8">
      <div className="mb-6">
        <h2 className="text-2xl font-semibold mb-1">Logo &amp; coordonnées</h2>
        <p className="text-muted-foreground text-sm">
          Uploadez le logo Vertec pour remplacer le logo par défaut. Idéalement en{" "}
          <strong>2 versions</strong> : une version sombre (à afficher sur les fonds clairs) et
          une version monochrome claire (à afficher sur les fonds sombres). Si vous n&apos;uploadez
          qu&apos;une seule version, elle sera utilisée sur les deux thèmes.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <LogoSlot
          title="Logo — fond sombre"
          hint="Version claire / blanche du logo (utilisée quand le thème sombre est actif — par défaut)."
          preview={brand.logo_dark}
          previewBg="#1B1B1B"
          onChange={(f) => uploadLogo("dark", f)}
          onReset={() => resetLogo("dark")}
          disabled={!isAdmin || saving}
          inputRef={darkRef}
          testid="brand-dark"
        />
        <LogoSlot
          title="Logo — fond clair"
          hint="Version foncée / couleur du logo (utilisée quand le thème clair est actif)."
          preview={brand.logo_light}
          previewBg="#FFFFFF"
          onChange={(f) => uploadLogo("light", f)}
          onReset={() => resetLogo("light")}
          disabled={!isAdmin || saving}
          inputRef={lightRef}
          testid="brand-light"
        />
      </div>

      {!isAdmin && (
        <div className="mt-6 text-xs text-muted-foreground">
          Seul un administrateur peut modifier l&apos;identité visuelle.
        </div>
      )}
      <CompanyInfoSection isAdmin={isAdmin} />
    </Card>
  );
}

function LogoSlot({ title, hint, preview, previewBg, onChange, onReset, disabled, inputRef, testid }) {
  return (
    <div className="rounded-lg border border-vertec-borderDark p-5" data-testid={`logo-slot-${testid}`}>
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="font-semibold">{title}</h3>
          <p className="text-xs text-muted-foreground mt-1">{hint}</p>
        </div>
      </div>

      <div
        className="rounded-md border border-vertec-borderDark p-6 mb-4 flex items-center justify-center"
        style={{ background: previewBg, minHeight: 120 }}
      >
        {preview ? (
          <img src={preview} alt={title} style={{ maxHeight: 80, maxWidth: "100%", objectFit: "contain" }} />
        ) : (
          <div className="text-center">
            <div
              style={{
                width: 56, height: 56, background: "#F5C842", borderRadius: 6,
                display: "flex", alignItems: "center", justifyContent: "center",
                margin: "0 auto 8px", color: "#1B1B1B", fontWeight: 800, fontSize: 30,
              }}
            >D</div>
            <div className="text-[11px] uppercase tracking-widest" style={{ color: previewBg === "#FFFFFF" ? "#6b7280" : "#9CA3AF" }}>
              Logo par défaut
            </div>
          </div>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => onChange(e.target.files?.[0])}
        data-testid={`file-input-${testid}`}
      />
      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          className="border-vertec-borderDark gap-2"
          onClick={() => inputRef.current?.click()}
          disabled={disabled}
          data-testid={`btn-upload-${testid}`}
        >
          <Upload size={14} /> Uploader
        </Button>
        {preview && (
          <Button
            variant="outline"
            size="sm"
            className="border-vertec-borderDark gap-2 text-status-danger"
            onClick={onReset}
            disabled={disabled}
            data-testid={`btn-reset-${testid}`}
          >
            <Trash2 size={14} /> Réinitialiser
          </Button>
        )}
      </div>
    </div>
  );
}
